import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useAppDispatch, useAppSelector } from '@app/store/hooks';
import { paths } from '@shared/config/paths';
import { Button } from '@shared/ui/button/Button';
import { Card } from '@shared/ui/card/Card';
import { EmptyState } from '@shared/ui/empty-state/EmptyState';
import { ErrorState } from '@shared/ui/state/ErrorState';
import { Icon } from '@shared/ui/icon/Icon';
import { Input } from '@shared/ui/input/Input';
import { Skeleton } from '@shared/ui/skeleton/Skeleton';
import { Select } from '@shared/ui/select/Select';
import {
  deleteReceipt,
  fetchReceipts,
  selectReceipts,
  selectReceiptsError,
  selectReceiptsStatus,
  setReceiptStatus,
} from '../store/receiptsSlice';
import { ReceiptsTable } from '../components/ReceiptsTable';
import { ReceiptDetailsModal } from '../components/ReceiptDetailsModal';
import { ConfirmDeleteDialog } from '../components/ConfirmDeleteDialog';
import type { Receipt, ReceiptStatus } from '../types';

const statusOptions = [
  { value: 'all', label: 'Tous les statuts' },
  { value: 'validated', label: 'Validés' },
  { value: 'needs_review', label: 'À revoir' },
];

/** Skeleton de chargement de la table. */
function TableSkeleton() {
  return (
    <div
      data-testid="receipts-loading"
      className="flex flex-col gap-2 rounded-lg border border-slate-200 bg-white p-4"
    >
      {Array.from({ length: 5 }, (_, index) => (
        <div key={index} className="flex items-center gap-4 px-1 py-2.5">
          <Skeleton className="h-4 w-40" />
          <Skeleton className="h-4 w-24" />
          <Skeleton className="ml-auto h-4 w-16" />
          <Skeleton className="h-6 w-20 rounded-full" />
        </div>
      ))}
    </div>
  );
}

/**
 * Page « Receipts » : header, barre d'outils (recherche, filtre, ajout) et
 * table des tickets validés, avec états vide / chargement / erreur.
 */
export function ReceiptsPage() {
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const receipts = useAppSelector(selectReceipts);
  const status = useAppSelector(selectReceiptsStatus);
  const error = useAppSelector(selectReceiptsError);
  const urlQuery = searchParams.get('q') ?? '';
  const query = urlQuery;
  const [statusFilter, setStatusFilter] = useState<'all' | ReceiptStatus>('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [filtersOpen, setFiltersOpen] = useState(false);

  // Modales + tickets en cours de traitement (actions désactivées).
  const [viewing, setViewing] = useState<Receipt | null>(null);
  const [deleting, setDeleting] = useState<Receipt | null>(null);
  const [pendingIds, setPendingIds] = useState<ReadonlySet<string>>(new Set());
  const [actionError, setActionError] = useState<string | null>(null);

  // Charge les receipts depuis l'API au premier affichage.
  useEffect(() => {
    if (status === 'idle') {
      void dispatch(fetchReceipts());
    }
  }, [dispatch, status]);

  const loading = status === 'loading' || status === 'idle';

  const filtered = receipts.filter((receipt) => {
    const matchesQuery = receipt.merchant.toLowerCase().includes(query.trim().toLowerCase());
    const matchesStatus = statusFilter === 'all' || receipt.status === statusFilter;
    const matchesDateFrom = !dateFrom || receipt.date >= dateFrom;
    const matchesDateTo = !dateTo || receipt.date <= dateTo;
    return matchesQuery && matchesStatus && matchesDateFrom && matchesDateTo;
  });
  const hasFilters = Boolean(query || statusFilter !== 'all' || dateFrom || dateTo);
  const setQuery = (nextQuery: string) => {
    const nextParams = new URLSearchParams(searchParams);
    if (nextQuery) {
      nextParams.set('q', nextQuery);
    } else {
      nextParams.delete('q');
    }
    setSearchParams(nextParams, { replace: true });
  };
  const clearFilters = () => {
    setQuery('');
    setStatusFilter('all');
    setDateFrom('');
    setDateTo('');
  };

  /** Valide / remet en revue un ticket. */
  const handleToggleStatus = async (receipt: Receipt) => {
    const next = receipt.status === 'validated' ? 'needs_review' : 'validated';
    setActionError(null);
    setPendingIds((prev) => new Set(prev).add(receipt.id));
    try {
      await dispatch(setReceiptStatus({ id: receipt.id, status: next })).unwrap();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Impossible de modifier le statut.');
    } finally {
      setPendingIds((prev) => {
        const nextSet = new Set(prev);
        nextSet.delete(receipt.id);
        return nextSet;
      });
    }
  };

  /** Supprime un ticket après confirmation. */
  const handleConfirmDelete = async () => {
    if (!deleting) return;
    setActionError(null);
    setPendingIds((prev) => new Set(prev).add(deleting.id));
    try {
      await dispatch(deleteReceipt(deleting.id)).unwrap();
      setDeleting(null);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Impossible de supprimer le ticket.');
    } finally {
      setPendingIds((prev) => {
        const nextSet = new Set(prev);
        nextSet.delete(deleting.id);
        return nextSet;
      });
    }
  };

  return (
    <section className="flex w-full max-w-full flex-col gap-6 overflow-x-hidden">
      {/* En-tête */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Tickets</h1>
          <p className="text-sm text-slate-500">
            Importez vos tickets et factures ; l'IA en extrait les données, vous validez.
          </p>
        </div>
        <Link to={paths.receiptNew} tabIndex={-1}>
          <Button>
            <Icon name="plus" size={16} />
            Ajouter un ticket
          </Button>
        </Link>
      </div>

      {/* Barre d'outils */}
      <Card className="p-4">
        <button
          type="button"
          onClick={() => setFiltersOpen((prev) => !prev)}
          className="flex w-full items-center justify-between gap-1.5 text-sm font-medium text-slate-700 sm:pointer-events-none sm:cursor-default"
          aria-expanded={filtersOpen}
          aria-controls="filters-panel"
        >
          <span className="flex items-center gap-1.5">
            <Icon name="filter" size={14} className="text-slate-400" />
            Filtres
            {hasFilters ? (
              <span className="ml-1 rounded-full bg-emerald-100 px-1.5 py-0.5 text-xs font-semibold text-emerald-600">
                actif
              </span>
            ) : null}
          </span>
          <Icon
            name="chevron-down"
            size={16}
            className={`text-slate-400 transition-transform duration-300 sm:hidden ${filtersOpen ? 'rotate-180' : ''}`}
          />
        </button>

        {/* Wrapper animé : grid-rows 0fr -> 1fr, transition fluide sans connaître la hauteur */}
        <div
          className={`grid transition-[grid-template-rows] duration-300 ease-in-out sm:!grid-rows-[1fr] ${
            filtersOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
          }`}
        >
          <div id="filters-panel" className="overflow-hidden">
            <div className="my-3 flex flex-wrap items-end gap-3">
              <div className="relative w-full min-w-0 flex-1 sm:w-auto sm:max-w-xs sm:min-w-56">
                <Icon
                  name="search"
                  size={16}
                  className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-slate-400"
                />
                <input
                  type="search"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Rechercher un commerçant…"
                  aria-label="Rechercher un ticket"
                  className="h-10 w-full rounded-lg border border-slate-300 bg-white pr-3 pl-9 text-sm text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-emerald-500 focus:outline-2 focus:outline-emerald-600"
                />
              </div>
              <div className="flex w-full flex-wrap items-end gap-2 sm:w-auto sm:flex-nowrap">
                <Select
                  aria-label="Filtrer par statut"
                  options={statusOptions}
                  value={statusFilter}
                  onChange={(event) => setStatusFilter(event.target.value as 'all' | ReceiptStatus)}
                  className={`w-full sm:w-auto sm:min-w-40 ${
                    statusFilter === 'all' ? 'text-slate-400' : 'text-slate-900'
                  }`}
                />
              </div>
              <div aria-hidden="true" className="hidden h-10 w-px bg-slate-200 sm:block" />
              <div className="flex w-full flex-wrap items-end gap-2 sm:w-auto sm:flex-nowrap">
                <div className="flex w-full items-center gap-1.5 rounded-lg border border-slate-200 bg-slate-50/50 px-2.5 py-1 sm:w-auto">
                  <Input
                    id="date-from"
                    label="Du"
                    labelPosition="inline"
                    type="date"
                    value={dateFrom}
                    max={dateTo || undefined}
                    onChange={(event) => setDateFrom(event.target.value)}
                    className={
                      dateFrom
                        ? 'text-slate-900'
                        : 'text-slate-400 [&::-webkit-datetime-edit]:text-slate-400'
                    }
                  />
                  <Icon
                    name="arrow-left"
                    size={13}
                    className="shrink-0 rotate-180 transform text-slate-300"
                  />
                  <Input
                    id="date-to"
                    label="Au"
                    labelPosition="inline"
                    type="date"
                    value={dateTo}
                    min={dateFrom || undefined}
                    onChange={(event) => setDateTo(event.target.value)}
                    className={
                      dateTo
                        ? 'text-slate-900'
                        : 'text-slate-400 [&::-webkit-datetime-edit]:text-slate-400'
                    }
                  />
                </div>
              </div>
              {hasFilters ? (
                <Button
                  variant="ghost"
                  onClick={clearFilters}
                  className="text-[13px] text-slate-500 sm:ml-auto sm:w-auto"
                >
                  <Icon name="x" size={14} />
                  Réinitialiser
                </Button>
              ) : null}
            </div>
          </div>
        </div>
      </Card>

      {/* Contenu */}
      {loading ? (
        <TableSkeleton />
      ) : status === 'failed' ? (
        <ErrorState
          title="Impossible de charger les tickets"
          message={error ?? 'Une erreur est survenue.'}
          action={
            <Button variant="secondary" onClick={() => void dispatch(fetchReceipts())}>
              Réessayer
            </Button>
          }
        />
      ) : receipts.length === 0 ? (
        <EmptyState
          icon="receipt"
          title="Aucun ticket pour le moment"
          description="Ajoutez votre premier ticket ou facture pour lancer l'extraction automatique des données."
          action={
            <Link to={paths.receiptNew} tabIndex={-1}>
              <Button>
                <Icon name="plus" size={16} />
                Ajouter mon premier ticket
              </Button>
            </Link>
          }
        />
      ) : filtered.length === 0 ? (
        <EmptyState
          icon="search"
          title="Aucun ticket ne correspond aux filtres"
          description="Modifiez ou réinitialisez vos filtres pour élargir la recherche."
          action={
            <Button variant="secondary" onClick={clearFilters}>
              Réinitialiser les filtres
            </Button>
          }
        />
      ) : (
        <>
          {actionError ? (
            <p role="alert" className="text-sm text-red-600">
              {actionError}
            </p>
          ) : null}
          <ReceiptsTable
            receipts={filtered}
            pendingIds={pendingIds}
            onView={setViewing}
            onToggleStatus={handleToggleStatus}
            onDelete={setDeleting}
          />
        </>
      )}

      {/* Modales */}
      <ReceiptDetailsModal receipt={viewing} onClose={() => setViewing(null)} />
      <ConfirmDeleteDialog
        receipt={deleting}
        deleting={deleting !== null && pendingIds.has(deleting.id)}
        onConfirm={() => void handleConfirmDelete()}
        onCancel={() => setDeleting(null)}
      />
    </section>
  );
}
